import { AuthStorage } from "utils/auth-storage";
import { React } from "react";
import { Button, Modal, Alert } from "antd";
import { useState } from "react";
import { ProjectServices } from "apis/Services/ProjectService";
import { useParams } from "react-router-dom";
import { reloadProjects } from "redux/project-reducer";
import { DeleteOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";

function BulkDelete({
  projectDetails,
  projectsAccessLevelValue,
  checkedRows,
  setCheckedRows,
  onDelete,
  itemsPendingApprovals = projectDetails?.itemsPendingApprovals || [],
  beginSyncActivity,
  finishSyncActivity,
  failSyncActivity,
  allowDeleteNumberedSubmittals = false,
}) {
  const { company_id, id } = useParams();
  const [isProcore, setIsProcore] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredData, setFilteredData] = useState();
  const dispatch = useDispatch();
  const pendingApprovalEntryIds = new Set(itemsPendingApprovals.map(String));
  const pendingApprovalCount = (filteredData || []).filter((item) =>
    pendingApprovalEntryIds.has(String(item.entryID)),
  ).length;

  const handleDeleting = () => {
    if (projectsAccessLevelValue > 2) {
      setIsProcore(undefined);
      const selectedData = [];
      let matchedSelectedCount = 0;
      let hasRestrictedSelection = false;

      const entryIDs = Object.keys(checkedRows).filter(
        (entryID) => checkedRows[entryID],
      );

      // Filtering data that matches entryID
      for (const key of Object.keys(projectDetails.content)) {
        const division = projectDetails.content[key];
        const subDivisions = Object.values(division);
        for (const subDivision of subDivisions) {
          for (const item of subDivision) {
            const itemIdentity = String(
              item.entryID || item.tempId || item.id || "",
            );

            if (entryIDs.includes(itemIdentity)) {
              matchedSelectedCount += 1;
              if (allowDeleteNumberedSubmittals || item.specNumber === "") {
                // Adding only koncurent entries to the request
                selectedData.push(item);
              } else {
                // Procore flag to show the warning
                hasRestrictedSelection = true;
              }
            }
          }
        }
      }

      if (selectedData.length === 0 && matchedSelectedCount > 0) {
        setIsProcore("All");
      } else if (hasRestrictedSelection) {
        setIsProcore("Some");
      }

      setFilteredData(selectedData);
      setIsModalOpen(true);
    }
  };

  function handleOk() {
    const selectedIdentifiers = filteredData.map((item) =>
      String(item.entryID || item.tempId || item.id || ""),
    );
    const persistedEntryIDs = filteredData
      .map((item) => String(item.entryID || ""))
      .filter((entryID) => entryID && !entryID.startsWith("new-"));
    const syncToken = beginSyncActivity?.("Deleting submittals");
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");
    let authData = {
      company_id: company_id,
      project_id: id,
      code,
      loginEmail: email,
    };
    setIsModalOpen(false);

    if (!persistedEntryIDs.length) {
      onDelete?.(selectedIdentifiers);
      setCheckedRows([]);
      setIsModalOpen(false);
      setIsProcore();
      finishSyncActivity?.(syncToken);
      return;
    }

    ProjectServices.deleteRow({ entryID: persistedEntryIDs }, authData)
      .then((res) => {
        onDelete?.(selectedIdentifiers);
        dispatch(reloadProjects());
        setCheckedRows([]);
        finishSyncActivity?.(syncToken);
      })
      .catch((err) => {
        failSyncActivity?.(
          syncToken,
          "Failed to delete submittals. You may no longer be synced to the latest version.",
        );
      })
      .finally(() => {
        setIsModalOpen(false);
        setIsProcore();
      });
  }

  function handleCancel() {
    setIsModalOpen(false);
    setIsProcore();
  }

  return (
    <div>
      <div
        onClick={handleDeleting}
        className=" d-flex justify-content-left gap-2 align-items-center"
      >
        <DeleteOutlined style={{ fontSize: "15px" }} />
        Bulk Delete
      </div>
      <Modal
        open={isModalOpen}
        width={700}
        onOk={isProcore !== "All" ? handleOk : null}
        onCancel={handleCancel}
        footer={
          isProcore === "All"
            ? [
                <Button key="cancel" onClick={handleCancel}>
                  Cancel
                </Button>,
                <Button key="delete" type="primary" danger disabled>
                  Delete
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={handleCancel}>
                  Cancel
                </Button>,
                <Button key="delete" type="primary" onClick={handleOk} danger>
                  Delete
                </Button>,
              ]
        }
      >
        <div className="">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fw-bold pt-2">
              Are you sure you want to delete all selected items?
            </h5>
          </div>
          <div className=" d-flex flex-column">
            <p>
              You can still recover deleted items from the deleted items page.
            </p>
            <Alert
              message="Please note deleting something that exists in Procore will end up
              bringing it back. To delete permanently please delete in Procore."
              type="warning"
            />

            {pendingApprovalCount > 0 && (
              <Alert
                message={
                  pendingApprovalCount === 1
                    ? "1 selected item is pending approval. Deleting it will also remove it from the pending approvals list."
                    : `${pendingApprovalCount} selected items are pending approval. Deleting them will also remove them from the pending approvals list.`
                }
                type="warning"
                showIcon
              />
            )}

            {!allowDeleteNumberedSubmittals && isProcore === "Some" ? (
              <Alert
                // message="Some of the selected items have a submittal number, those items will not be deleted."
                message="Items with a submittal number will not be deleted."
                type="warning"
                showIcon
              />
            ) : !allowDeleteNumberedSubmittals && isProcore === "All" ? (
              <Alert
                message="All selected items have a submittal number, you need to remove the submittal number from these items before proceeding."
                type="error"
                showIcon
              />
            ) : (
              <></>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default BulkDelete;
